<script setup>
import { RadioGroup, RadioGroupOption } from "@headlessui/vue";
import { CheckIcon } from "@heroicons/vue/20/solid/index.js";
import { Link } from "@inertiajs/vue3";
import { ref } from "vue";

defineProps({
    tiers: Array,
});

const frequencies = [
    { value: "monthly", label: "Monthly", priceSuffix: "/month" },
    { value: "annually", label: "Annually", priceSuffix: "/month" },
];

const frequency = ref(frequencies[1]);
</script>

<template>
    <div class="mt-40 sm:mt-64">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="mx-auto max-w-2xl sm:text-center">
                <h2
                    class="text-base/7 font-semibold text-brand-dark dark:text-brand-accent"
                >
                    Get started
                </h2>
                <p
                    class="mt-2 text-pretty text-4xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 sm:text-balance sm:text-5xl"
                >
                    Choose the right plan for you
                </p>
            </div>
        </div>
        <div class="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
            <div class="mt-16 flex justify-center">
                <fieldset aria-label="Payment frequency">
                    <RadioGroup
                        v-model="frequency"
                        class="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs/5 font-semibold ring-1 ring-inset ring-gray-200"
                    >
                        <RadioGroupOption
                            as="template"
                            v-for="option in frequencies"
                            :key="option.value"
                            :value="option"
                            v-slot="{ checked }"
                        >
                            <div
                                :class="[
                                    checked
                                        ? 'bg-brand-dark dark:bg-brand-light text-white dark:text-midnight'
                                        : 'text-gray-500 dark:text-gray-200',
                                    'cursor-pointer rounded-full px-2.5 py-1',
                                ]"
                            >
                                {{ option.label }}
                            </div>
                        </RadioGroupOption>
                    </RadioGroup>
                </fieldset>
            </div>
            <div
                class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:grid-cols-3 lg:max-w-4xl"
            >
                <div
                    v-for="tier in tiers"
                    :key="tier.id"
                    :class="[
                        tier.featured
                            ? 'ring-2 ring-brand-dark dark:ring-brand-light'
                            : 'ring-1 ring-gray-200',
                        'rounded-3xl p-8',
                    ]"
                >
                    <h3
                        :id="tier.id"
                        :class="[
                            tier.featured
                                ? 'text-brand-dark dark:text-brand-accent'
                                : 'text-gray-900 dark:text-gray-100',
                            'text-lg/8 font-semibold',
                        ]"
                    >
                        {{ tier.name }}
                    </h3>
                    <p class="mt-4 text-sm/6 text-gray-600 dark:text-gray-300">
                        {{ tier.description }}
                    </p>
                    <p class="mt-6 flex items-baseline gap-x-1">
                        <span
                            class="text-4xl font-semibold tracking-tight text-gray-900 dark:text-gray-100"
                            >{{ tier.price[frequency.value] }}</span
                        >
                        <span
                            class="text-sm/6 font-semibold text-gray-600 dark:text-gray-300"
                            >{{ frequency.priceSuffix }}</span
                        >
                    </p>
                    <p class="mt-2 flex flex-col items-baseline gap-y-2">
                        <span
                            v-if="frequency.value === 'annually'"
                            class="text-accent text-sm"
                            >Billed annually</span
                        >
                        <span
                            v-if="frequency.value === 'annually'"
                            class="text-gray-600 dark:text-gray-300 text-base font-semibold leading-6"
                            >Get two months FREE!</span
                        >
                    </p>
                    <Link
                        :href="route('register', { plan: tier.name })"
                        :aria-describedby="tier.id"
                        :class="[
                            tier.featured
                                ? 'bg-brand-dark dark:bg-brand-light text-white dark:text-midnight shadow-xs hover:bg-brand-dark-hover dark:hover:bg-brand-accent'
                                : 'text-brand-dark dark:text-brand-accent ring-1 ring-inset ring-light-to dark:ring-brand-accent hover:ring-brand-light',
                            'mt-6 block rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-dark',
                        ]"
                        prefetch
                        >Start for FREE</Link
                    >
                    <ul
                        role="list"
                        class="mt-8 space-y-3 text-sm/6 text-gray-600 dark:text-gray-300"
                    >
                        <li
                            v-for="feature in tier.features"
                            :key="feature"
                            class="flex gap-x-3"
                        >
                            <CheckIcon
                                class="h-6 w-5 flex-none text-brand-dark dark:text-brand-accent"
                                aria-hidden="true"
                            />
                            <span
                                class="*:text-sm *:text-gray-400"
                                v-html="feature"
                            ></span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
